import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { DAY_IN_SECONDS, SECOND_IN_MILLISECONDS, YEAR_IN_DAYS } from '@vaultsfyi/common/constants'
import { formatAmount, formatDate } from '@vaultsfyi/common/utils'
import { type SVGProps, useMemo } from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Loader } from '../common/loader'
import { ChartContainer } from './chartContainer'
import { ChartTooltip } from './chartTooltip'
import { ChartWatermark } from './chartWatermark'
import { getPredictedValues } from './utils/getPredictedChartValues'

type ChartData = { timestamp: number; value: number; predictedValue?: number; index?: number }

interface AreaChartProps {
  data: Array<ChartData>
  asset: Vault['asset']
  annualApy: number
  className?: string
}

export function HistoricalValueAreaChart({ data, asset, annualApy, className }: AreaChartProps) {
  if (data.length === 0) {
    return (
      <ChartContainer className={cn('h-32 min-h-32', className)}>
        <Loader />
      </ChartContainer>
    )
  }

  const chartDataWithPrediction = useMemo(() => {
    const lastDataPoint = data[data.length - 1]

    const predictedValues = getPredictedValues({
      initialValue: lastDataPoint.value,
      apy: annualApy,
      startTimestamp: lastDataPoint.timestamp + DAY_IN_SECONDS * SECOND_IN_MILLISECONDS,
      durationInDays: YEAR_IN_DAYS,
    })

    const connectionPoint = {
      timestamp: lastDataPoint.timestamp,
      predictedValue: lastDataPoint.value,
      value: undefined,
      index: data.length - 1,
    }

    const historicalData = data.map((point, idx) => ({ ...point, index: idx }))
    const predictionData = predictedValues.map((point, idx) => ({
      timestamp: point.timestamp,
      predictedValue: point.value,
      value: undefined,
      index: data.length + (idx * 0.5 * data.length) / predictedValues.length,
    }))

    return [...historicalData, connectionPoint, ...predictionData]
  }, [data, annualApy])

  const maxValue = Math.max(...chartDataWithPrediction.map((d) => d.value ?? 0))

  return (
    <ChartContainer className={cn('h-32 min-h-32', className)}>
      <ResponsiveContainer>
        <AreaChart data={chartDataWithPrediction}>
          <defs>
            <linearGradient id="historicalValueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-blue))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-blue))" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="historicalValuePredictedFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-blue))" stopOpacity={0.4} />
              <stop offset="100%" stopColor="rgb(var(--chart-blue))" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="index"
            type="number"
            tickLine={false}
            interval={'preserveStartEnd'}
            domain={['dataMin', 'dataMax']}
            tick={(props) => <HistoricalValueTick {...props} chartData={chartDataWithPrediction} />}
          />
          <YAxis
            tickMargin={0}
            tickLine={false}
            tickFormatter={(value) => formatAmount(value, { notation: 'compact', minimumFractionDigits: 2 })}
            domain={['dataMin', maxValue * 1.05]}
            interval="preserveStartEnd"
            label={{
              value: `${asset.symbol}`,
              style: { textAnchor: 'middle' },
              angle: -90,
              position: 'left',
              offset: 0,
            }}
          />
          <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border))" />
          <Tooltip
            content={({ payload }) => <HistoricalValueChartTooltip data={payload?.[0]?.payload} asset={asset} />}
          />
          <Area
            dataKey="value"
            type="stepAfter"
            isAnimationActive={false}
            stroke="rgb(var(--chart-blue))"
            strokeOpacity={1}
            strokeWidth={2}
            fill="url(#historicalValueFill)"
          />
          <Area
            dataKey="predictedValue"
            type="monotone"
            isAnimationActive={false}
            stroke="rgb(var(--chart-blue))"
            strokeOpacity={0.4}
            strokeWidth={2}
            strokeDasharray="4 4"
            fill="url(#historicalValuePredictedFill)"
          />
          <ChartWatermark />
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

interface HistoricalValueChartTooltipProps {
  data: ChartData | undefined
  asset: Vault['asset']
}

const HistoricalValueChartTooltip = ({ data, asset }: HistoricalValueChartTooltipProps) => {
  if (!data) {
    return null
  }
  return (
    <ChartTooltip>
      <span>{formatDate({ timestamp: data.timestamp, showYear: true })}</span>
      <p className="font-semibold">
        {data.predictedValue !== undefined
          ? `Predicted position value: ${formatAmount(data.predictedValue, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${asset.symbol}`
          : `Position value: ${formatAmount(data.value, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${asset.symbol}`}
      </p>
    </ChartTooltip>
  )
}

interface HistoricalValueTickProps extends SVGProps<SVGElement> {
  payload: Record<string, any>
  chartData: ChartData[]
}

const HistoricalValueTick = (props: HistoricalValueTickProps) => {
  const { x, y, payload, chartData } = props
  const dataPoint = chartData.find((d) => d.index === payload.value)
  if (!dataPoint) return null

  const date = new Date(dataPoint.timestamp)
  const now = new Date()
  const isCurrentYear = date.getFullYear() === now.getFullYear()
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: isCurrentYear ? undefined : 'numeric',
  }).format(dataPoint.timestamp)

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={8} textAnchor="start" className="text-xs">
        {formattedDate}
      </text>
    </g>
  )
}
